import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { SpecialityDTOSchema, CreateSpecialitySchema, UpdateSpecialitySchema } from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const SpecialitiesResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(SpecialityDTOSchema),
});

export function useSpecialities(options: { enabled?: boolean } = {}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["specialities"],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: "/api/v1/specialities",
      });
      return SpecialitiesResponseSchema.parse(json).data;
    },
    enabled: options.enabled !== false,
  });
}

export function useCreateSpeciality() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: z.infer<typeof CreateSpecialitySchema>) =>
      backend({ method: "POST", url: "/api/v1/specialities", data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["specialities"] });
      toast.success(t("templates.specialityCreated"));
    },
  });
}

export function useUpdateSpeciality() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: z.infer<typeof UpdateSpecialitySchema> }) =>
      backend({ method: "PATCH", url: `/api/v1/specialities/${id}`, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["specialities"] });
      toast.success(t("templates.specialityUpdated"));
    },
  });
}

export function useDeleteSpeciality() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "DELETE", url: `/api/v1/specialities/${id}` }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["specialities"] }),
  });
}
